import {Input, Modal} from 'antd'
import {doc_editCodiDocument_Api, docs_removeCodiDocument_Api} from 'apis'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Formik} from 'formik'
import filterOption from 'helpers/filterOption'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {useAppConfig} from 'modules/local'
import {LocalContext} from 'modules/local/LocalContext'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {BsChevronRight} from 'react-icons/bs'
import {UseConfirmDeleteAction} from 'views/Document/CodiDocumentContext'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {showConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {AdvancedActionGroups} from 'views/NewTemplate/components/AdvancedActionGroups'
import {DefaultActionButton} from 'views/NewTemplate/components/AdvancedActionGroups/ActionGroup'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import CodiContext from './CodiContext'

const ActionTypes = Object.freeze({
  REMOVE_CODI: 'remove-codi',
  UPDATE_OVERVIEW_INFO: 'update-overview-info',
})

const formSchema = {
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
        }),
      },
    ],
  },
  description: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 5,
          placeholder: 'description',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  document_type: {
    title: 'document type',
    children: [
      {
        name: 'document_type',
        component: createControlledFormField({
          className: 'w-full',
          Component: SelectWidget,
          placeholder: 'document type',
          withProps: ({ name, onChange }) => ({
            onChange: (value, option) => {
              onChange(createValue(name, option))
            },
          }),
          withValues: (values) => ({
            options: values?.document_types,
          }),
        }),
      },
    ],
  },
  categories: {
    title: 'categories',
    children: [
      {
        name: 'categories',
        component: ({ name, value, onChange }) => (
          <Pure input={[value]}>
            <Translate>
              {(t) => (
                <LocalContext.Consumer>
                  {({ appConfig }) => {
                    const { categories } = appConfig || {}
                    const options = Array.from(categories || []).map(
                      (category) => ({
                        label: t(getTitle(category)),
                        value: category?.id,
                        ...category,
                      })
                    )
                    return (
                      <SelectWidget
                        value={value}
                        mode="multiple"
                        className="w-full"
                        showSearch={true}
                        allowClear={true}
                        options={options}
                        filterOption={filterOption}
                        onChange={(value, option) => {
                          onChange(createValue(name, value))
                        }}
                      />
                    )
                  }}
                </LocalContext.Consumer>
              )}
            </Translate>
          </Pure>
        ),
      },
    ],
  },
}

const UpdateInfo = ({ item, onSubmit }) => {
  const t = useTranslate()

  const appConfig = useAppConfig()

  const validate = (values) => {
    const { title, document_type } = values ?? {}

    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }
    if (_.isEmpty(document_type?.id)) {
      errors.document_type = t('required field')
    }
    return errors
  }

  const initialValues = useMemo(() => {
    const document_types = Array.from(appConfig?.document_types ?? []).map(
      (item) => ({
        ...item,
        value: item?.id,
        label: item?.title,
      })
    )
    const { document_type, categories } = item || {}
    return {
      ...item,
      document_types,
      document_type: _.find(document_types, { value: document_type?.id }),
      categories: _.isArray(categories)
        ? categories.map((category) => category?.id)
        : [],
    }
  }, [item, appConfig])

  return (
    <Formik
      validate={validate}
      onSubmit={(values) => {
        const changed = shallowDiff(values, initialValues)
        const { categories, document_type, document_types, ...rest } =
          changed ?? {}
        onSubmit(
          _.omitBy(
            {
              ...rest,
              document_type_id: document_type?.id ?? undefined,
              categories: categories
                ? Array.from(categories).join(',')
                : undefined,
            },
            _.isUndefined
          )
        )
      }}
      enableReinitialize={true}
      initialValues={initialValues}>
      <div className="flex flex-col space-y-2">
        <AntdConfigWrapper>
          <FieldsFactory
            formSchema={[
              formSchema.title,
              formSchema.description,
              formSchema.document_type,
              formSchema.categories,
            ]}
          />
        </AntdConfigWrapper>
        <FormActionBar submitText="save" />
      </div>
    </Formik>
  )
}

const UseUpdateCodiDocument = ({
  children,
  codi_document_id,
  onSuccess = NullAsDefault,
}) => {
  const t = useTranslate()
  const asyn_data = useAsyncAction({
    apiInfo: doc_editCodiDocument_Api,
    query: bindQueryParam({ id: codi_document_id }),
    onError: notifyOnError(t),
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      onSuccess()
    },
  })
  return _.isFunction(children) ? children(asyn_data) : children
}

const ManageCodiActions = ({ title, description }) => {
  const t = useTranslate()

  const { item, onRefresh = NullAsDefault } = useContext(CodiContext)

  const codi_document_id = item?.id

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const onCancel = () => {
    setAction({})
  }

  const { handleAsyncAction: onDelete } = useAsyncAction({
    apiInfo: docs_removeCodiDocument_Api,
    query: bindQueryParam({ id: codi_document_id }),
    onError: notifyOnError(t),
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      onCancel()
    },
  })

  const options = {
    overview: {
      label: 'overview',
      value: ActionTypes.UPDATE_OVERVIEW_INFO,
      description: 'codi information',
    },
    remove: {
      title: 'remove codi',
      label: 'remove',
      value: ActionTypes.REMOVE_CODI,
      danger: true,
      description: 'delete current codi',
      confirm: {
        title: 'delete',
        message: 'are you sure',
      },
    },
  }

  const handleAction = (action) => {
    switch (action) {
      case ActionTypes.REMOVE_CODI:
        showConfirmDialog({
          translate: t,
          onOk: () => {
            onDelete()
          },
        })
        break
      case ActionTypes.UPDATE_OVERVIEW_INFO:
        setAction({
          type: action,
          value: item,
        })
        break
      default:
        break
    }
  }

  const modal = useMemo(() => {
    let params = {}
    let modal_params = {}
    let Component = NullAsDefault
    switch (action.type) {
      case ActionTypes.UPDATE_OVERVIEW_INFO:
        modal_params = {
          title: (
            <div className="text-center text-color-000 font-bold uppercase">
              {t('update information')}
            </div>
          ),
        }
        params = {
          item,
        }
        Component = (props) => (
          <UseUpdateCodiDocument
            onSuccess={() => {
              onCancel()
              onRefresh()
            }}
            codi_document_id={codi_document_id}>
            {({ handleAsyncAction }) => (
              <UpdateInfo
                onSubmit={(values) => {
                  handleAsyncAction(values)
                }}
                {...props}
              />
            )}
          </UseUpdateCodiDocument>
        )
        break
      default:
        break
    }
    return (
      <Modal
        className="custom-modal"
        footer={null}
        onCancel={onCancel}
        open={action.type}
        destroyOnHidden={true}
        {...modal_params}>
        <Component
          {...{ onCancel }}
          {...params}
        />
      </Modal>
    )
  }, [action])

  return (
    <AdvancedActionGroups.ActionGroup
      title={title}
      description={description}>
      <AdvancedActionGroups.SimpleActionButton
        title={'information book'}
        label={'overview'}
        suffix={<BsChevronRight size={11} />}
        onClick={() => {
          handleAction(ActionTypes.UPDATE_OVERVIEW_INFO)
        }}
        description={'change codi basic information'}
      />
      {[options.remove].map(
        ({ value, title, description, confirm = false, ...rest }, index) => (
          <AdvancedActionGroups.SimpleActionWrapper
            key={index}
            title={title}
            description={description}>
            <UseConfirmDeleteAction
              {...(confirm ?? {})}
              apiInfo={docs_removeCodiDocument_Api}
              query={bindQueryParam({ id: codi_document_id })}
              onSuccess={() => {
                successNotify(NotificationActionTypes.UPDATE, t)
                onCancel()
              }}>
              {({ isLoading = false, confirm = NullAsDefault }) => (
                <DefaultActionButton
                  onClick={() => {
                    confirm()
                  }}
                  isLoading={isLoading}
                  description={description}
                  {...rest}
                />
              )}
            </UseConfirmDeleteAction>
          </AdvancedActionGroups.SimpleActionWrapper>
        )
      )}
      {modal}
    </AdvancedActionGroups.ActionGroup>
  )
}

export default ManageCodiActions
